<template>
  <article
    class="product-card tw-group tw-relative tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-card tw-border tw-border-slate-200/80 tw-soft-card tw-transition-all tw-duration-200"
    :class="{
      'tw-ring-2 tw-ring-emerald-400/70': isBestDeal,
      'product-card--pressed': isPressed,
      'product-card--compact': isCarousel,
    }"
    :data-product-key="productKey"
    @touchstart.passive="isPressed = true"
    @touchend="isPressed = false"
    @touchcancel="isPressed = false"
    @mousedown="isPressed = true"
    @mouseup="isPressed = false"
    @mouseleave="isPressed = false"
  >
    <div
      v-if="isBestDeal"
      class="tw-absolute tw-left-3 tw-top-3 tw-z-10 tw-rounded-full tw-bg-emerald-600 tw-px-3 tw-py-1 tw-text-xs tw-font-bold tw-text-white tw-shadow-sm"
    >
      {{ $l('bestDeal') }}
    </div>

    <button
      type="button"
      class="tw-pressable tw-absolute tw-right-3 tw-top-3 tw-z-10 tw-inline-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-200 tw-bg-white/95 tw-text-slate-500 tw-shadow-sm tw-transition hover:tw-border-rose-300 hover:tw-text-rose-500 sm:tw-h-10 sm:tw-w-10"
      :class="{ 'tw-border-rose-300 tw-text-rose-500': isWatched }"
      style="min-width:44px;min-height:44px"
      :title="isWatched ? 'הסר מרשימת מעקב' : 'הוסף לרשימת מעקב'"
      @click="toggleWatchlist"
    >
      <outline-icon name="heart" size="md" />
    </button>

    <a
      class="tw-block tw-bg-slate-50 tw-px-5 tw-pt-10"
      :href="primaryStore.itemUrl"
      target="_blank"
      @click="sendClickedData(primaryStore)"
    >
      <img
        class="tw-h-44 tw-w-full tw-object-contain tw-transition-transform tw-duration-200 group-hover:tw-scale-[1.02] sm:tw-h-56"
        :src="itemImageUrl"
        alt="item image"
      >
    </a>

    <div class="tw-flex tw-flex-1 tw-flex-col tw-gap-4 tw-p-5">
      <div class="tw-space-y-2">
        <h3 class="tw-line-clamp-2 tw-min-h-[3rem] tw-text-base tw-font-semibold tw-leading-6 tw-text-slate-900">
          {{ itemTitle }}
        </h3>
        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
          <verified-price-badge v-if="hasVerifiedPrice" />
          <span
            v-if="savingsInfo"
            class="tw-rounded-full tw-bg-brand-soft tw-px-2 tw-py-0.5 tw-text-[0.6875rem] tw-font-semibold tw-text-brand-dark sm:tw-px-2.5 sm:tw-py-1 sm:tw-text-xs"
          >
            {{ savingsInfo.text }}
          </span>
        </div>
      </div>

      <div class="tw-mt-auto tw-space-y-2">
        <div
          v-for="(store, ind) in visibleStores"
          :key="`${store.id}-${store.country}-${store.retailer}-${ind}`"
          class="store-row tw-flex tw-items-center tw-gap-2 tw-rounded-2xl tw-border tw-border-slate-100 tw-bg-white tw-p-2.5 sm:tw-gap-3 sm:tw-p-3 tw-transition hover:tw-border-brand/30 hover:tw-bg-slate-50"
          :class="{ 'tw-border-emerald-200 tw-bg-emerald-50/60': ind === 0 && stores.length > 1 }"
        >
          <a
            class="store-row__link tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2 tw-no-underline sm:tw-gap-3"
            :href="store.itemUrl"
            target="_blank"
            rel="noopener noreferrer"
            @click="onStoreLinkClick(store, $event)"
          >
            <img
              class="tw-h-4 tw-w-6 tw-shrink-0 tw-rounded-[2px] tw-object-cover tw-opacity-80"
              :src="'/img/flags/' + store.country + '.png'"
              alt="country flag"
            >
            <img
              class="tw-h-6 tw-w-6 tw-shrink-0 tw-rounded-md tw-object-contain tw-opacity-80"
              :src="'/img/' + store.retailer + '.png'"
              alt="marketplace"
              v-b-popover.hover.blur.html.top="{
                animation: true,
                variant: 'light',
                content: returnContent(store),
              }"
              :title="store.retailer"
            >
            <span
              class="store-row__price tw-ms-auto tw-shrink-0 tw-whitespace-nowrap tw-py-1 tw-text-base tw-font-bold tw-tabular-nums tw-leading-tight tw-text-slate-950 group-hover:tw-text-brand sm:tw-text-lg"
              dir="ltr"
            >
              {{ currencySign || '₪' }}{{ store.price.calcTotalPrice }}
            </span>
          </a>
          <div
            class="store-row__actions tw-flex tw-shrink-0 tw-items-center tw-gap-0.5 tw-text-slate-500"
            @click.stop
          >
            <button
              type="button"
              class="store-row__action-btn tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-p-1.5 tw-transition hover:tw-bg-slate-100 hover:tw-text-brand"
              v-b-popover.click.blur.html.top="{
                animation: true,
                variant: 'info',
                content: shippingBreakdown(store),
              }"
              title="פירוט מחיר"
              @click.stop
            >
              <outline-icon name="truck" size="sm" />
            </button>
            <ShareNetwork
              network="WhatsApp"
              :url="store.itemUrl"
              title="Compopo just found you an awesome product!"
              :description="store.title"
            >
              <span
                class="store-row__action-btn tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-p-1.5 tw-transition hover:tw-bg-slate-100 hover:tw-text-brand"
                @click.stop
              >
                <outline-icon name="share" size="sm" />
              </span>
            </ShareNetwork>
          </div>
        </div>

        <!-- Show more / less toggle for mobile -->
        <button
          v-if="stores.length > MOBILE_STORE_LIMIT && !isCarousel"
          type="button"
          class="tw-pressable tw-w-full tw-min-h-[44px] tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-600 tw-transition hover:tw-bg-slate-100 lg:tw-hidden"
          @click="storesExpanded = !storesExpanded"
        >
          {{ storesExpanded ? $l('showLess') : $l('showAllStores').replace('{count}', stores.length) }}
        </button>
      </div>
    </div>
  </article>
</template>

<script>
import axios from "axios";
import { getSavingsInfo, getShippingBreakdown } from '@/utils/priceUtils';
import {
    isInWatchlist,
    toggleWatchlist,
    buildWatchlistEntry,
} from '@/utils/watchlist';
import OutlineIcon from './OutlineIcon.vue';
import VerifiedPriceBadge from './VerifiedPriceBadge.vue';

export default {
    name: 'ShoppingItemCard',
    components: {
        OutlineIcon,
        VerifiedPriceBadge,
    },
    props: {
        itemType: String,
        itemTitle: String,
        itemUrl: String,
        storeCountry: String,
        currencySign: String,
        itemCalcTotalPrice: String,
        itemImageUrl: String,
        storesCount: Number,
        isCarousel: Boolean,
        items: Array,
        isBestDeal: {
            type: Boolean,
            default: false,
        },
        shipToCountry: {
            type: String,
            default: '',
        },
        productKey: {
            type: String,
            default: '',
        },
        searchQuery: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isWatched: false,
            storesExpanded: false,
            isPressed: false,
            MOBILE_STORE_LIMIT: 3,
        };
    },
    computed: {
        stores() {
            const list = this.items || [];
            return this.storesCount ? list.slice(0, this.storesCount) : list;
        },
        visibleStores() {
            if (this.storesExpanded || window.innerWidth >= 1024) {
                return this.stores;
            }
            return this.stores.slice(0, this.MOBILE_STORE_LIMIT);
        },
        primaryStore() {
            return this.stores[0] || {};
        },
        hasVerifiedPrice() {
            return this.stores.some((store) => {
                const price = store.price || {};
                return store.hasShippingInfo || (price.calcPrice && price.calcTotalPrice);
            });
        },
        savingsInfo() {
            return getSavingsInfo(this.stores, this.currencySign);
        },
        watchlistProduct() {
            return {
                otherStores: this.items,
                itemType: this.itemType,
            };
        },
    },
    watch: {
        productKey: {
            immediate: true,
            handler() {
                this.refreshWatchState();
            },
        },
    },
    mounted() {
        this.$root.$on('watchlist-updated', this.refreshWatchState);
    },
    beforeDestroy() {
        this.$root.$off('watchlist-updated', this.refreshWatchState);
    },
    methods: {
        refreshWatchState() {
            this.isWatched = this.productKey
                ? isInWatchlist(this.productKey)
                : false;
        },
        toggleWatchlist() {
            if (!this.productKey || !this.items || !this.items.length) {
                return;
            }
            const entry = buildWatchlistEntry(
                this.watchlistProduct,
                this.productKey,
                this.searchQuery
            );
            toggleWatchlist(entry);
            this.refreshWatchState();
            this.$root.$emit('watchlist-updated');
        },
        shippingBreakdown(store) {
            return getShippingBreakdown(store, this.shipToCountry, this.currencySign);
        },
        onStoreLinkClick(store, event) {
            if (!store?.itemUrl) {
                event.preventDefault();
                return;
            }
            this.sendClickedData(store);
        },
        sendClickedData(product) {
            axios({
                method: "post",
                url: '/api/v1/goToProduct/',
                data: {
                    'url': product.itemUrl,
                    'title': product.title,
                    'price': product.price.calcTotalPrice + product.price.calcCurrency
                }
            })
            .catch(() => {});
        },
        returnContent(storeIndex) {
            switch (storeIndex.retailer) {
                case 'eBay':
                    return `Seller: ${storeIndex.seller.username}</br> Feedback Score: <span style=color:#6b87f7>${storeIndex.seller.feedbackScore}</span>
                            </br> Positive feedback: <span style=color:#6b87f7>${storeIndex.seller.feedbackPercentage}%</span>`;
                case 'AliExpress':
                    return `Seller: ${storeIndex.seller.username}</br> Seller's Store: <span style=color:#fd892a>${storeIndex.seller.shopUrl}</span>`;
                default:
                    return '';
            }
        },
        popoverTitleColor(retailer) {
            switch (retailer) {
                case 'eBay':
                    return '#6b87f7';
                case 'AliExpress':
                    return '#fd892a';
                default:
                    return '#6190e8';
            }
        },
    },
};
</script>

<style scoped>
.product-card {
  will-change: transform, box-shadow;
}

@media (hover: hover) {
  .product-card:hover {
    transform: translateY(-4px);
    box-shadow:
      0 4px 6px rgba(15, 23, 42, 0.06),
      0 16px 32px rgba(15, 23, 42, 0.1);
  }
}

.product-card--pressed {
  transform: translateY(-2px) scale(0.99);
  box-shadow:
    0 4px 6px rgba(15, 23, 42, 0.08),
    0 12px 28px rgba(15, 23, 42, 0.14);
}

.store-row__link {
  min-width: 0;
  color: inherit;
  cursor: pointer;
}

.store-row__link:hover .store-row__price {
  color: #2563eb;
}

.store-row__price {
  direction: ltr;
  unicode-bidi: isolate;
}

.store-row__action-btn {
  min-width: 28px;
  min-height: 28px;
}

@media (max-width: 640px) {
  .store-row__action-btn {
    min-width: 36px;
    min-height: 36px;
  }
}
</style>
